import { faDatabase, faHelmetSafety, faServer } from '@fortawesome/pro-solid-svg-icons'
import { EditableLabelContent, LabelContent } from 'V2Components'
import {
  isOrgAccount,
  useCloudAccounts,
  useCloudDetailsProvider,
  useCloudsResources
} from 'features/clouds'
import { getResourceName } from 'features/resources'
import { Label, TargetIcon } from 'procyon-ui'
import React from 'react'
import { AccountNotificationChannels } from '../../../AccountNotificationChannels'
import { SSHPublicKeyLabelContent } from '../SSHPublickeyLabelContent'
import { getHTMLDataTestID } from 'infra/utils'

const AWSAccount = ({}) => {
  const { cloud: account } = useCloudDetailsProvider()

  const { getAccountTargets } = useCloudsResources()
  const { getChildAccounts } = useCloudAccounts()

  const isOrg = isOrgAccount(account)
  const childAccounts = getChildAccounts(account)

  const targets = getAccountTargets(account)

  const IconTypeMap = {
    AppRole: 'APPROLE_AWS',
    Server: 'SERVER',
    Database: 'DATABASE',
    KubeCluster: 'KUBE_CLUSTERS',
    KubeNamespace: 'KUBE_NAMESPACES',
    RDPServer: 'RDP_SERVER'
  }

  return (
    <div className='w-[100%]'>
      <LabelContent
        data-testid={'name'}
        dataTestId={getHTMLDataTestID('cloud-details', 'p', 'name')}
        content={getResourceName(account)}
        title='Name'
      />
      <LabelContent
        data-testid={'description'}
        dataTestId={getHTMLDataTestID('cloud-details', 'p', 'description')}
        title='Description'
        content={account.Spec.Description}
        showEmptyLabel
      />
      <LabelContent
        data-testid={'notification-channel'}
        dataTestId={getHTMLDataTestID('cloud-details', 'p', 'notification-channels')}
        title='Notification Channels'
        content={
          <div className='flex gap-2 flex-wrap'>
            <AccountNotificationChannels
              account={account}
              Fallback={<Label text='No Channels' />}
            />
          </div>
        }
      />
      {account.Spec.AwsSpec.AssumeRoleARN && (
        <>
          <LabelContent
            data-testid={'assume-role-arn'}
            dataTestId={getHTMLDataTestID('cloud-details', 'p', 'arn')}
            title='Assume Role ARN'
            content={account.Spec.AwsSpec.AssumeRoleARN}
            showEmptyLabel
          />
          <LabelContent
            data-testid={'external-id'}
            dataTestId={getHTMLDataTestID('cloud-details', 'p', 'external-id')}
            title='External ID'
            content={account.Spec.AwsSpec.ExternalID}
          />
        </>
      )}
      {account.Spec.AwsSpec.OuID && (
        <LabelContent
          data-testid={'ouid'}
          dataTestId={getHTMLDataTestID('cloud-details', 'p', 'ou-id')}
          content={account.Spec.AwsSpec.OuID}
          title='OU ID'
        />
      )}
      <LabelContent
        data-testid={'account-id'}
        dataTestId={getHTMLDataTestID('cloud-details', 'p', 'account-id')}
        content={account.Spec.AccountID}
        title='Account ID'
      />
      {isOrg && (
        <LabelContent
          data-testid={'child-account'}
          dataTestId={getHTMLDataTestID('cloud-details', 'p', 'child-accounts')}
          content={
            <Label icon={<TargetIcon type='AWS_CHILD' />} text={childAccounts?.length || 0} />
          }
          title='Child Accounts'
        />
      )}
      {!isOrg && (
        <>
          {Object.entries(targets).map(([targetType, config]) => {
            const count = targets[targetType]?.length || 0
            if (count === 0) return null

            return (
              <LabelContent
                key={targetType}
                data-testid={targetType.toLowerCase()}
                dataTestId={getHTMLDataTestID('cloud-details', 'p', targetType)}
                title={targetType}
                content={
                  <Label
                    icon={<TargetIcon className='rounded' type={IconTypeMap[targetType]} />}
                    text={count}
                  />
                }
              />
            )
          })}
        </>
      )}
      <SSHPublicKeyLabelContent />
    </div>
  )
}

export { AWSAccount }
